import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, XCircle, Clock, Flame, Wrench, User, Calendar,
  ChevronDown, ChevronRight, FileText, AlertTriangle, RefreshCw, Plus
} from 'lucide-react';

const API = `${getApiUrl()}/api`;
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const MPA_LABELS = {
  tres_modeste: { label: 'Tres Modeste (70%)', color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', icon: CheckCircle2 },
  modeste: { label: 'Modeste (50%)', color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', icon: CheckCircle2 },
  non_eligible: { label: 'Non eligible', color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', icon: XCircle },
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const EligibiliteCard = ({ entry, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const mpa = entry.mpa || {};
  const reno = entry.reno_energie || {};
  const travaux = entry.travaux_supplementaires || {};
  const mpaConfig = MPA_LABELS[mpa.resultat] || {};

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${open ? 'border-slate-600' : 'border-slate-700/50'}`}
      data-testid={`eligibilite-card-${entry.entry_id}`}>
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-800/30 transition-colors"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${mpaConfig.bg || 'bg-slate-700/50'}`}>
          {mpaConfig.icon ? <mpaConfig.icon className={`w-5 h-5 ${mpaConfig.color}`} /> : <FileText className="w-5 h-5 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-bold text-sm ${mpaConfig.color || 'text-slate-300'}`}>
              {mpaConfig.label || 'Qualification'}
            </span>
            {entry.statut_final && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">
                {typeof entry.statut_final === 'object' ? entry.statut_final.label : entry.statut_final}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{timeAgo(entry.date_qualification)}</span>
            {entry.telepro_nom && <span className="flex items-center gap-1"><User className="w-3 h-3" />{entry.telepro_nom}</span>}
          </div>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-slate-500 shrink-0" /> : <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" />}
      </button>

      {/* Detail */}
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-700/50 pt-3">
          {/* MPA */}
          {mpa.resultat && (
            <div className={`rounded-lg p-3 border ${mpaConfig.border || 'border-slate-700'} ${mpaConfig.bg || 'bg-slate-800/50'}`}>
              <h5 className="text-xs font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> MaPrimeRenov
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                {mpa.zone && <div><span className="text-slate-500 text-xs">Zone</span><p className="text-white">{mpa.zone === 'idf' ? 'Ile-de-France' : 'Province'}</p></div>}
                {mpa.nb_personnes && <div><span className="text-slate-500 text-xs">Foyer</span><p className="text-white">{mpa.nb_personnes} pers.</p></div>}
                {mpa.rfr !== undefined && <div><span className="text-slate-500 text-xs">RFR</span><p className="text-white">{Number(mpa.rfr).toLocaleString('fr-FR')} EUR</p></div>}
                {mpa.taux_prise_en_charge && <div><span className="text-slate-500 text-xs">Taux</span><p className="text-white">{mpa.taux_prise_en_charge}%</p></div>}
                {mpa.enveloppe_max && <div><span className="text-slate-500 text-xs">Enveloppe max</span><p className="text-white">{Number(mpa.enveloppe_max).toLocaleString('fr-FR')} EUR</p></div>}
                {mpa.montant_restant !== undefined && <div><span className="text-slate-500 text-xs">Restant</span><p className="text-emerald-400 font-bold">{Number(mpa.montant_restant).toLocaleString('fr-FR')} EUR</p></div>}
                {mpa.type_bien && <div><span className="text-slate-500 text-xs">Bien</span><p className="text-white">{mpa.type_bien === 'maison' ? 'Maison' : 'Appartement'}</p></div>}
                {mpa.statut_logement && <div><span className="text-slate-500 text-xs">Statut</span><p className="text-white">{mpa.statut_logement}</p></div>}
              </div>
            </div>
          )}

          {/* Reno Energie */}
          {reno.produits && reno.produits.length > 0 && (
            <div className="rounded-lg p-3 border border-orange-500/20 bg-orange-500/5">
              <h5 className="text-xs font-semibold text-orange-400 uppercase mb-2 flex items-center gap-1">
                <Flame className="w-3.5 h-3.5" /> Renovation energetique
              </h5>
              {reno.categorie_mpr && (
                <p className="text-sm text-orange-300 mb-2">Categorie : {reno.categorie_mpr}</p>
              )}
              <div className="space-y-1">
                {reno.produits.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-300">{p.nom || p.label || p}</span>
                    {p.montant_cee > 0 && <span className="text-orange-400">{Number(p.montant_cee).toLocaleString('fr-FR')} EUR CEE</span>}
                  </div>
                ))}
              </div>
              {reno.total_cee > 0 && (
                <div className="mt-2 pt-2 border-t border-orange-500/20 flex justify-between text-sm font-bold">
                  <span className="text-slate-400">Total CEE</span>
                  <span className="text-orange-400">{Number(reno.total_cee).toLocaleString('fr-FR')} EUR</span>
                </div>
              )}
            </div>
          )}

          {/* Travaux supplementaires */}
          {travaux.selectionnes && travaux.selectionnes.length > 0 && (
            <div className="rounded-lg p-3 border border-purple-500/20 bg-purple-500/5">
              <h5 className="text-xs font-semibold text-purple-400 uppercase mb-2 flex items-center gap-1">
                <Wrench className="w-3.5 h-3.5" /> Travaux supplementaires
              </h5>
              <div className="space-y-1">
                {travaux.selectionnes.map((t, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-300">{t.label || t.nom || t}</span>
                    {t.prix > 0 && <span className="text-purple-400">{Number(t.prix).toLocaleString('fr-FR')} EUR</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {entry.notes && (
            <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
              <p className="text-sm text-slate-400 italic">"{entry.notes}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function EligibilitesTab({ clientId, onNewEligibilite }) {
  const [data, setData] = useState({ eligibilites: [], simulation_mpa: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/pipeline/client/row_${clientId}/eligibilites`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const entries = data.eligibilites || [];
  const simMpa = data.simulation_mpa;

  // Build combined timeline: eligibilites entries + legacy simulation_mpa
  const timeline = [...entries];
  if (simMpa && !entries.some(e => e.entry_id)) {
    // Add legacy MPA as a fallback entry if no new entries exist
    if (simMpa.historique && simMpa.historique.length > 0) {
      simMpa.historique.forEach((h, i) => {
        timeline.push({
          entry_id: `legacy_mpa_${i}`,
          date_qualification: h.date,
          telepro_nom: simMpa.telepro_nom || '',
          mpa: {
            resultat: h.resultat || simMpa.resultat,
            zone: simMpa.zone,
            nb_personnes: simMpa.nb_personnes,
            rfr: simMpa.rfr,
            taux_prise_en_charge: simMpa.taux_prise_en_charge,
            enveloppe_max: simMpa.enveloppe_max,
            montant_restant: h.montant_restant || simMpa.montant_restant,
            type_bien: simMpa.type_bien,
            statut_logement: simMpa.statut_logement,
          },
          reno_energie: { produits: h.produits_interesses?.map(p => ({ nom: p })) || [] },
          travaux_supplementaires: {},
          notes: simMpa.notes || '',
          statut_final: '',
          _legacy: true
        });
      });
    } else if (simMpa.resultat) {
      timeline.push({
        entry_id: 'legacy_mpa_main',
        date_qualification: simMpa.date_simulation,
        telepro_nom: simMpa.telepro_nom || '',
        mpa: {
          resultat: simMpa.resultat,
          zone: simMpa.zone,
          nb_personnes: simMpa.nb_personnes,
          rfr: simMpa.rfr,
          taux_prise_en_charge: simMpa.taux_prise_en_charge,
          enveloppe_max: simMpa.enveloppe_max,
          montant_restant: simMpa.montant_restant,
          type_bien: simMpa.type_bien,
          statut_logement: simMpa.statut_logement,
        },
        reno_energie: { produits: simMpa.produits_interesses?.map(p => ({ nom: p })) || [] },
        travaux_supplementaires: {},
        notes: simMpa.notes || '',
        statut_final: '',
        _legacy: true
      });
    }
  }

  // Sort by date desc
  timeline.sort((a, b) => new Date(b.date_qualification || 0) - new Date(a.date_qualification || 0));

  if (loading) {
    return (
      <div className="p-5 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4" data-testid="eligibilites-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          Eligibilites & Travaux Supplementaires
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{timeline.length} qualification(s)</span>
          {onNewEligibilite && (
            <button
              data-testid="new-eligibilite-btn"
              onClick={onNewEligibilite}
              className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Nouvelle eligibilite
            </button>
          )}
        </div>
      </div>

      {timeline.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
          <AlertTriangle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Aucune qualification enregistree pour ce client</p>
          <p className="text-slate-500 text-sm mt-1">Les eligibilites apparaitront ici apres une qualification</p>
        </div>
      ) : (
        <div className="space-y-3">
          {timeline.map((entry, idx) => (
            <EligibiliteCard key={entry.entry_id || idx} entry={entry} defaultOpen={idx === 0} />
          ))}
        </div>
      )}
    </div>
  );
}
